import React, { useEffect } from 'react';
import SEO from '../SEO';
import seoData from '../../seoData';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { CheckCircle, ArrowRight, Shield, Settings, Cog } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';
import { useNavigate } from 'react-router-dom';

const EnginesCasings = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title={seoData["services/engines-casings"]?.title} description={seoData["services/engines-casings"]?.description} path="/services/engines-casings" keywords={seoData["services/engines-casings"]?.keywords || []} />

      <PageHero
        badge="Machinery & Equipment"
        title="Engine & Casing Repair"
        description="Engine block, turbine casing, and equipment housing repair using Belzona epoxy composites. Restore worn components to OEM specifications without welding, machining, or replacement. In-situ cold-applied repair contractor."
        backgroundKey="engines-casings"
        buttonText="Get In Touch"
      />

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Problems We Solve</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {['Engine block cracks and corrosion damage',
              'Turbine casing erosion and clearance loss',
              'Gearbox housing wear and bearing seat damage',
              'Compressor casing sealing surface deterioration',
              'Diesel engine cooling jacket corrosion',
              'Split-line and mating surface damage'].map((challenge, index) => (
              <div key={index} className="flex items-start p-4 bg-blue-50 rounded-lg">
                <Settings className="text-blue-600 mr-3 mt-0.5 flex-shrink-0" size={20} />
                <span className="text-gray-700 text-sm">{challenge}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Our Solutions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Card className="border-l-4 border-l-blue-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Cog className="text-blue-600 mr-3 flex-shrink-0" size={22} />
                  Engine Block Repair
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Crack repair, corrosion rebuild, and cooling jacket restoration for diesel, gas, and electric motor housings. Belzona metal repair composites restore engine blocks without welding heat or distortion.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-blue-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Cog className="text-blue-600 mr-3 flex-shrink-0" size={22} />
                  Turbine Casing Restoration
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Internal erosion repair, clearance restoration, and split-line rebuild for steam and gas turbine casings. Precision application to restore OEM tolerances.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-blue-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Cog className="text-blue-600 mr-3 flex-shrink-0" size={22} />
                  Gearbox & Compressor Housing Repair
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Bearing seat restoration, mounting surface repair, and housing crack repair for gearboxes, compressors, and blower casings using cold-applied epoxy composites.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-blue-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Cog className="text-blue-600 mr-3 flex-shrink-0" size={22} />
                  Split-Line & Mating Surface Rebuild
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Precision rebuild of split-line surfaces, gasket faces, and mating surfaces to achieve proper sealing and alignment without machining.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Why Choose BCS</h2>
              <div className="space-y-3">
                {['No hot work — cold-applied repair eliminates welding distortion',
                  'In-situ repair — no equipment removal or transport required',
                  'Restore OEM tolerances and specifications',
                  '70% cost savings vs. new casing or engine replacement',
                  'Rapid cure systems for time-critical repairs',
                  'Manufacturer-backed warranties',
                  '25+ years machinery repair experience',
                  'Emergency repair response available'].map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="text-green-700 mr-3 mt-0.5 flex-shrink-0" size={18} />
                    <span className="text-gray-700 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-blue-900 text-white p-8 rounded-lg">
              <Shield className="mb-4" size={48} />
              <h3 className="text-xl font-bold mb-4">Belzona Products We Use</h3>
              <div className="space-y-3">
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 1111 (Super Metal)</p>
                  <p className="text-xs text-slate-500 mt-1">General engine and casing rebuild</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 1131 (Bearing Metal)</p>
                  <p className="text-xs text-slate-500 mt-1">Bearing seat and housing repair</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 1221 (Super E-Metal)</p>
                  <p className="text-xs text-slate-500 mt-1">Corrosion and erosion repair</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 1341 (Supermetalglide)</p>
                  <p className="text-xs text-slate-500 mt-1">Clearance restoration coating</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Engine or Casing Damaged? We Restore It.</h2>
          <p className="text-blue-100 mb-8">Contact us for a free equipment assessment. We repair engines, turbines, and casings on-site without welding.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => window.location.href = 'tel:+19493719948'}>
              Call (949) 371-9948
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-blue-800" onClick={() => navigate('/#contact')}>
              Request Assessment <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EnginesCasings;
